import React from 'react';
import { motion } from 'framer-motion';
import { FacebookIcon, InstagramIcon, YoutubeIcon, LinkedinIcon, MailIcon, PhoneIcon, MapPinIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type FooterProps = {
  language: Language;
  onLanguageChange: (lang: Language) => void;
};
const footerLinks = {
  ua: {
    company: {
      title: 'Компанія',
      links: ['Про нас', 'Якість', 'Клієнти', "Кар'єра"]
    },
    materials: {
      title: 'Матеріали',
      links: ['Каталоги', 'Завантажити', 'Відео', 'Інструкції']
    },
    partners: {
      title: 'Партнерам',
      links: ['Партнери', 'Стати партнером', 'B2B портал', 'Підтримка']
    },
    contact: {
      title: 'Контакти',
      email: 'info@chamaeleon-produktion.de',
      phone: '+49-(0)6221/520440',
      address: 'Rudolf-Diesel-Straße 8a, 69115 Heidelberg, Germany'
    }
  },
  en: {
    company: {
      title: 'Company',
      links: ['About Us', 'Quality', 'Clients', 'Careers']
    },
    materials: {
      title: 'Materials',
      links: ['Catalogs', 'Downloads', 'Video', 'Instructions']
    },
    partners: {
      title: 'For Partners',
      links: ['Partners', 'Become a Partner', 'B2B Portal', 'Support']
    },
    contact: {
      title: 'Contact',
      email: 'info@chamaeleon-produktion.de',
      phone: '+49-(0)6221/520440',
      address: 'Rudolf-Diesel-Straße 8a, 69115 Heidelberg, Germany'
    }
  }
};
const socialLinks = [{
  icon: FacebookIcon,
  href: '#',
  label: 'Facebook'
}, {
  icon: InstagramIcon,
  href: '#',
  label: 'Instagram'
}, {
  icon: YoutubeIcon,
  href: '#',
  label: 'YouTube'
}, {
  icon: LinkedinIcon,
  href: '#',
  label: 'LinkedIn'
}];
export function Footer({
  language,
  onLanguageChange
}: FooterProps) {
  const t = footerLinks[language];
  return <footer className="relative bg-chamaleon-black pt-16 pb-8 overflow-hidden" data-id="element-65">
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-chamaleon-yellow/30 to-transparent" data-id="element-66" />

      {/* Background */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
      backgroundSize: '100px 100px'
    }} data-id="element-67" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-68">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12 mb-12" data-id="element-69">
          {/* Logo & Social */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1 mb-8 lg:mb-0" data-id="element-70">
            <a href="#" className="inline-flex items-center gap-2 mb-6 h-8 sm:h-10" data-id="element-71">
              <img src="https://cdn.magicpatterns.com/uploads/dPC9v62jFFRaqiBcJdHaJe/chamaeleon_logo_white-yellow_RGB_копія.png" alt="CHAMÄLEON" className="h-full w-auto object-contain" data-id="element-72" />
            </a>

            {/* Social Links */}
            <div className="flex items-center gap-3" data-id="element-73">
              {socialLinks.map(social => {
              const Icon = social.icon;
              return <motion.a key={social.label} href={social.href} aria-label={social.label} whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.95
              }} className="w-10 h-10 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center text-gray-400 hover:text-chamaleon-yellow hover:border-chamaleon-yellow/30 transition-all" data-id="element-74">
                    <Icon className="w-5 h-5" data-id="element-75" />
                  </motion.a>;
            })}
            </div>
          </div>

          {/* Company */}
          <div data-id="element-76">
            <h4 className="text-white font-semibold mb-4" data-id="element-77">{t.company.title}</h4>
            <ul className="space-y-3" data-id="element-78">
              {t.company.links.map(link => <li key={link} data-id="element-79">
                  <a href="#" className="text-gray-400 hover:text-chamaleon-yellow transition-colors text-sm" data-id="element-80">
                    {link}
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Materials */}
          <div data-id="element-81">
            <h4 className="text-white font-semibold mb-4" data-id="element-82">
              {t.materials.title}
            </h4>
            <ul className="space-y-3" data-id="element-83">
              {t.materials.links.map(link => <li key={link} data-id="element-84">
                  <a href="#" className="text-gray-400 hover:text-chamaleon-yellow transition-colors text-sm" data-id="element-85">
                    {link}
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Partners */}
          <div data-id="element-86">
            <h4 className="text-white font-semibold mb-4" data-id="element-87">
              {t.partners.title}
            </h4>
            <ul className="space-y-3" data-id="element-88">
              {t.partners.links.map(link => <li key={link} data-id="element-89">
                  <a href="#" className="text-gray-400 hover:text-chamaleon-yellow transition-colors text-sm" data-id="element-90">
                    {link}
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Contact */}
          <div data-id="element-91">
            <h4 className="text-white font-semibold mb-4" data-id="element-92">{t.contact.title}</h4>
            <ul className="space-y-3" data-id="element-93">
              <li data-id="element-94">
                <a href={`mailto:${t.contact.email}`} className="flex items-center gap-2 text-gray-400 hover:text-chamaleon-yellow transition-colors text-sm" data-id="element-95">
                  <MailIcon className="w-4 h-4" data-id="element-96" />
                  {t.contact.email}
                </a>
              </li>
              <li data-id="element-97">
                <a href={`tel:${t.contact.phone}`} className="flex items-center gap-2 text-gray-400 hover:text-chamaleon-yellow transition-colors text-sm" data-id="element-98">
                  <PhoneIcon className="w-4 h-4" data-id="element-99" />
                  {t.contact.phone}
                </a>
              </li>
              <li data-id="element-100">
                <span className="flex items-start gap-2 text-gray-400 text-sm" data-id="element-101">
                  <MapPinIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-102" />
                  <span data-id="element-103">{t.contact.address}</span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5" data-id="element-104">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-105">
            {/* Copyright */}
            <p className="text-gray-500 text-sm" data-id="element-106">
              © {new Date().getFullYear()} CHAMÄLEON.{' '}
              {language === 'ua' ? 'Усі права захищено.' : 'All rights reserved.'}
            </p>

            {/* Language Switcher */}
            <div className="flex items-center gap-2" data-id="element-107">
              <span className="text-gray-500 text-sm mr-2" data-id="element-108">
                {language === 'ua' ? 'Мова:' : 'Language:'}
              </span>
              {(['en', 'ua'] as Language[]).map(lang => <button key={lang} onClick={() => onLanguageChange(lang)} className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${language === lang ? 'bg-chamaleon-yellow text-chamaleon-black font-medium' : 'text-gray-400 hover:text-white bg-white/5'}`} data-id="element-109">
                  {lang.toUpperCase()}
                </button>)}
            </div>
          </div>
        </div>
      </div>
    </footer>;
}